import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractBaseFieldComponent } from '@shared/components/form-elements/abstract-field-base';

@Component({
  selector: 'cx-input-code',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './input-code.component.html',
  styleUrls: ['./input-code.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCodeComponent),
      multi: true,
    },
  ],
  host: {
    '[attr.id]': 'null',
  },
})
export class InputCodeComponent extends AbstractBaseFieldComponent implements ControlValueAccessor, OnInit {
  @Input() length = 6;
  @Input() mode: 'text' | 'numeric' = 'text';
  @Input() id = this.generateId();
  @Input() label = '';
  @Input() hintText = '';
  @Input() invalid?: boolean;
  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef>;

  codeForm!: FormGroup;

  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private hostElement = inject(ElementRef);

  get hostClasses(): string[] {
    const classList = (this.hostElement.nativeElement as HTMLElement).classList;
    return Array.from(classList);
  }

  get inputMode() {
    return this.mode === 'numeric' ? 'numeric' : 'text';
  }

  ngOnInit(): void {
    const controls: { [key: string]: FormControl } = {};

    for (let i = 0; i < this.length; i++) {
      controls[`digit${i}`] = new FormControl('', [Validators.required]);
    }

    this.codeForm = new FormGroup(controls);

    // Listen to form changes and propagate to parent
    this.codeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isComplete()) {
        const code = this.getCode();
        this.onChange(code);
        this.onTouched();
      }
    });
  }

  onInput(event: any, index: number): void {
    const input = event.target;
    let value: string = input.value;

    if (this.mode === 'numeric') {
      value = value.replace(/[^0-9]/g, '');
    } else {
      value = value.charAt(0);
    }

    input.value = value;
    this.codeForm.get(`digit${index}`)?.setValue(value);

    if (value && index < this.length - 1 && this.isBrowser) {
      this.codeInputs.get(index + 1)?.nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (this.isBrowser && event.key === 'Backspace' && !this.codeForm.get(`digit${index}`)?.value && index > 0) {
      this.codeInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.isBrowser) return;

    let pastedData = event.clipboardData?.getData('text') || '';
    if (this.mode === 'numeric') {
      pastedData = pastedData.replace(/[^0-9]/g, '');
    }

    if (pastedData.length !== this.length) return;

    event.preventDefault();

    Array.from(pastedData).forEach((char, index) => {
      this.codeForm.get(`digit${index}`)?.setValue(char);
    });

    this.codeInputs.last?.nativeElement.focus();
  }

  getCode(): string {
    return Array.from({ length: this.length }, (_, i) => this.codeForm.get(`digit${i}`)?.value || '').join('');
  }

  isComplete(): boolean {
    return Array.from({ length: this.length }, (_, i) => this.codeForm.get(`digit${i}`)?.value).every(
      val => val && val.length === 1,
    );
  }

  override writeValue(value: string): void {
    for (let i = 0; i < this.length; i++) {
      this.codeForm.get(`digit${i}`)?.setValue(value?.[i] ?? '');
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.codeForm.disable();
    } else {
      this.codeForm.enable();
    }
  }
}
